<template>
    <div id="adminview">
        <div class="header">
            <h1>ADMIN VIEW</h1>
            <button @click="logout" class="logout-button">
                🚪 Logout
            </button>
        </div>
        
        <div class="main">
            <!-- Tab Navigation -->
            <div class="tabs-nav">
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'questions' }" 
                    @click="activeTab = 'questions'"
                >
                    💬 Questions/Answers
                </button>
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'twitter' }" 
                    @click="activeTab = 'twitter'"
                >
                    🐦 Twitter Users
                </button>
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'whatsapp' }" 
                    @click="activeTab = 'whatsapp'"
                >
                    📱 WhatsApp Electoral
                </button>
            </div>

            <!-- Questions/Answers Tab -->
            <div v-if="activeTab === 'questions'" class="tab-content">
                <div class="messages-list">
                    <div v-for="message in chatHistory" :key="message._id" :id="'message-' + message._id"
                        class="message-duplex-container">
                        <HumanMessageComponent :text="message.q" :timestamp="message.timestamp"
                            :conversation_id="message.conversation_id" />
                        <KotBotMessageComponent @show-toast="showToast" :text="message.a" :qanda_id="message._id"
                            :has_feedback="!!message.feedback !== null" :timestamp="message.timestamp"
                            :conversation_id="message.conversation_id" :sources="message?.sources" :fromAdmin="true"
                            :has_admin_feedback="!!message.admin_feedback" />
                    </div>
                </div>
                <div class="pagination-controls">
                    <button @click="prevPage" :disabled="offset === 0">Previous</button>
                    <button @click="nextPage">Next</button>
                    <select v-model="limit" @change="fetchChatHistory">
                        <option v-for="n in [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- Twitter Users Tab -->
            <div v-if="activeTab === 'twitter'" class="tab-content">
                <TwitterOAuthManager @show-toast="showToast" />
            </div>

            <!-- WhatsApp Electoral Tab -->
            <div v-if="activeTab === 'whatsapp'" class="tab-content">
                <WhatsAppElectoralMessages @show-toast="showToast" />
            </div>
        </div>

        <!-- Toast notification -->
        <div v-if="toast.show" class="toast" :class="toast.type">
            {{ toast.message }}
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import HumanMessageComponent from '@/components/HumanMessageComponent.vue';
import KotBotMessageComponent from '@/components/KotBotMessageComponent.vue';
import TwitterOAuthManager from '@/components/TwitterOAuthManager.vue';
import WhatsAppElectoralMessages from '@/components/WhatsAppElectoralMessages.vue';

export default {
    name: "AdminScreen",
    components: {
        HumanMessageComponent,
        KotBotMessageComponent,
        TwitterOAuthManager,
        WhatsAppElectoralMessages
    },
    data() {
        return {
            activeTab: 'questions',
            chatHistory: [],
            offset: 0,
            limit: 10,
            toast: {
                show: false,
                message: '',
                type: 'info',
                timeout: null
            }
        };
    },
    methods: {
        async fetchChatHistory() {
            try {
                const response = await axios.get(process.env.VUE_APP_BASE_URL + `/api/v2/questions-admins/list/?offset=${this.offset}&limit=${this.limit}`, { withCredentials: true });
                this.chatHistory = response.data;
            } catch (error) {
                console.error("Error fetching chat history:", error);
                this.showToast('Error fetching chat history', 'error');
            }
        },
        nextPage() {
            this.offset += this.limit;
            this.fetchChatHistory();
        },
        prevPage() {
            if (this.offset > 0) {
                this.offset -= this.limit;
                this.fetchChatHistory();
            }
        },
        showToast(message, type = 'info') {
            // Clear any existing timeout
            if (this.toast.timeout) {
                clearTimeout(this.toast.timeout);
            }

            // Show the toast
            this.toast.show = true;
            this.toast.message = message;
            this.toast.type = type;

            // Set a timeout to hide the toast
            this.toast.timeout = setTimeout(() => {
                this.toast.show = false;
            }, 3000);
        },
        logout() {
            // Clear user data from localStorage
            localStorage.removeItem('user_email');
            localStorage.removeItem('user_name');
            localStorage.removeItem('user_permissions');
            localStorage.removeItem('user_profile_picture');
            localStorage.removeItem('user_subscription');

            this.showToast('Sesión cerrada exitosamente', 'success');

            // Redirect to login page after a short delay
            setTimeout(() => {
                this.$router.push('/login');
            }, 1000);
        }
    },
    mounted() {
        // Check if user is authenticated
        if (!localStorage.getItem('user_email')) {
            this.$router.push('/login');
            return;
        }
        
        this.fetchChatHistory();
    }
};
</script>

<style scoped>
#adminview {
    background: transparent;
    width: 100vw;
    height: 90%;
    padding: 24px;
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
}

.header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

#adminview h1 {
    font-weight: bold;
    text-align: center;
    padding: 24px;
    border-radius: 24px;
    background-color: rgba(50, 50, 50, 0.85);
    color: #a1344e;
    box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.6);
    text-shadow: 0.5px 0.5px 3px rgba(255, 255, 255, 0.5);
    margin: 0;
}

.logout-button {
    padding: 10px 20px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.logout-button:hover {
    background-color: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.main {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
}

.messages-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
    overflow-y: scroll;
    height: 90%;
}

.pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
}

.pagination-controls button {
    margin: 0 10px;
}

.pagination-controls select {
    margin-left: 10px;
}

.tabs-nav {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    border-bottom: 2px solid rgba(50, 50, 50, 0.5);
    padding-bottom: 10px;
}

.tab-button {
    padding: 10px 20px;
    background-color: rgba(50, 50, 50, 0.7);
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
}

.tab-button:hover {
    background-color: rgba(70, 70, 70, 0.8);
}

.tab-button.active {
    background-color: #a1344e;
    box-shadow: 0 2px 8px rgba(161, 52, 78, 0.3);
}

.tab-content {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
}

/* Toast notification */
.toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 20px;
    border-radius: 4px;
    color: white;
    font-weight: 500;
    z-index: 1000;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    min-width: 250px;
    text-align: center;
}

.toast.info {
    background-color: #2196F3;
}

.toast.success {
    background-color: #4CAF50;
}

.toast.warning {
    background-color: #FF9800;
}

.toast.error {
    background-color: #F44336;
}
</style>
